/**
 * @fileoverview Where an invitation link lands.
 *
 * Says whose garden it is before the Join button, because a link with no
 * explanation is a link nobody clicks twice. (The route sits inside the
 * Layout, so a signed-out visitor meets the sign-in screen first and comes
 * back to this address; reading the invitation itself needs no sign-in.)
 * Signed in as the invited person, one button joins; signed in as somebody
 * else, it says so rather than quietly failing.
 *
 * An invitation is taken up once and lasts a fortnight or so (the backend
 * says how long). One already taken up offers no Join: the invited person
 * is told they are already in, with a button into the garden, and anybody
 * else that it has been used. One that has run out says so, and to ask for
 * another.
 *
 * "Signed in as" is the address the person signs in with, the same one the
 * backend checks the invitation against.
 *
 * Where: the route /garden/join?token= (App.tsx), inside the Layout. The
 * invitation email is sent from the profile's garden people (inviteToGarden).
 * API: GET /garden-members/invitation?token= (readInvitation) on opening;
 * POST /garden-members/accept { token } (acceptInvitation) on Join or Open
 * the garden.
 * Settings: the invitation's lifetime is the backend's (expiryDays).
 * Words (en/es): garden.join.*. A failure on our side (a 500, no
 * connection; D31, lib/serverFailure) says the standard sentence, reported
 * to Sentry, with the support address as a link: in place of
 * garden.join.notFound when the invitation could not be read, and of the
 * server's words or garden.join.failed when joining did not go through.
 * Watch out: joining calls chooseGarden, which remembers the garden on this
 * device (localStorage) and reloads the whole page, so nothing after it
 * runs; the button keeps spinning until the reload.
 *
 * @module JoinGarden
 */

import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/shared/components/ui/button';
import Spinner from '@/shared/components/ui/Spinner';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { useAuth } from '@/shared/hooks/useAuth';
import { acceptInvitation, chooseGarden, readInvitation, type Invitation } from '@/shared/api/gardenMembers';
import { cn } from '@/lib/utils';
import { serverFailureText } from '@/shared/lib/serverFailure';
import { FailureText } from '@/shared/components/ServerFailure';

/**
 * The translation key for a refusal the backend gives a reason code for, so
 * the page says it in the reader's language; null for any other, which shows
 * the backend's own sentence.
 *
 * @param code - The `error` the accept route answered with, if any.
 * @returns A translation key, or null.
 */
function refusalKey(code: string | undefined): string | null {
  switch (code) {
    case 'EXPIRED': return 'garden.join.expired';
    case 'UNVERIFIED_EMAIL': return 'garden.join.unverified';
    case 'TAKEN': return 'garden.join.used';
    default: return null;
  }
}

/** The page at /garden/join?token=…: whose garden, and the button that joins it. */
export const JoinGarden: React.FC = () => {
  const { reactTranslate: t } = useTranslation();
  const [params] = useSearchParams();
  const { currentUser, mysqlUser } = useAuth();
  const token = params.get('token') ?? '';
  const [invitation, setInvitation] = React.useState<Invitation | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [joining, setJoining] = React.useState(false);

  React.useEffect(() => {
    if (!token) { setError(t('garden.join.noToken')); return; }
    let cancelled = false;
    readInvitation(token)
      .then((found) => { if (!cancelled) setInvitation(found); })
      .catch((err) => { if (!cancelled) setError(serverFailureText(err, 'Join garden') ?? t('garden.join.notFound')); });
    return () => { cancelled = true; };
  }, [token, t]);

  /**
   * Accepts the invitation and switches to that garden (a full page load).
   * For somebody already in it, the same call answers yes without changing
   * anything, so it is also how "Open the garden" gets the garden to open.
   * The reason is shown if it refuses.
   */
  const join = async (): Promise<void> => {
    setJoining(true);
    setError(null);
    try {
      const result = await acceptInvitation(token);
      chooseGarden(result.ownerId);
    } catch (err) {
      const data = (err as { response?: { data?: { error?: string; message?: string } } })?.response?.data;
      const key = refusalKey(data?.error);
      // On our side: the standard sentence, before any words the failure carried.
      setError(serverFailureText(err, 'Join garden') ?? (key ? t(key) : data?.message ?? t('garden.join.failed')));
      setJoining(false);
    }
  };

  const heading = 'font-semibold font-body';
  // The sign-in's address, which is what the invitation is checked against.
  const signedInAs = currentUser?.email ?? mysqlUser?.email ?? '';
  const wrongAccount = Boolean(invitation && signedInAs
    && signedInAs.trim().toLowerCase() !== invitation.invitedEmail.trim().toLowerCase());
  const notice = 'mt-6 rounded-sm border border-warning/40 bg-warning-tint px-3 py-2 text-sm text-warning';

  /**
   * The button that joins, or opens the garden for somebody already in it.
   *
   * @param label - What the button says.
   * @returns The button, with a spinner while it works.
   */
  const joinButton = (label: string): React.ReactElement => (
    <Button className="mt-6" size="lg" disabled={joining} onClick={() => void join()}>
      {joining ? <Spinner size={16} color="currentColor" /> : null}
      <span className={joining ? 'ml-2' : ''}>{label}</span>
    </Button>
  );

  /**
   * What the page offers under the invitation, in order of what matters:
   * used (in, or not theirs), then the wrong account, then run out, then Join.
   *
   * @param found - The invitation, as read.
   * @returns The notice or button for that state.
   */
  const action = (found: Invitation): React.ReactElement => {
    if (found.accepted && !wrongAccount) {
      return (
        <>
          <p className="mt-6 text-sm">{t('garden.join.alreadyIn', { name: found.ownerName })}</p>
          {joinButton(t('garden.join.open'))}
        </>
      );
    }
    if (found.accepted) return <p className={notice}>{t('garden.join.used')}</p>;
    if (wrongAccount) {
      return <p className={notice}>{t('garden.join.wrongAccount', { invited: found.invitedEmail, signedIn: signedInAs })}</p>;
    }
    if (found.expired) return <p className={notice}>{t('garden.join.expiredFrom', { name: found.ownerName, days: found.expiryDays })}</p>;
    return joinButton(t('garden.join.accept'));
  };

  return (
    <div className="mx-auto max-w-lg p-4 text-center">
      {error && !invitation ? (
        <p role="alert" className="mt-8 text-muted-foreground"><FailureText text={error} /></p>
      ) : !invitation ? (
        <div className="flex items-center justify-center gap-3 py-12 text-sm text-muted-foreground">
          <Spinner size={20} />{t('garden.join.loading')}
        </div>
      ) : (
        <>
          <h1 className={cn(heading, 'mt-8 text-2xl')}>{t('garden.join.title', { name: invitation.ownerName })}</h1>
          <p className="mt-3 text-muted-foreground">{t('garden.join.body')}</p>

          {action(invitation)}

          {error && <p role="alert" className="mt-3 text-sm text-destructive"><FailureText text={error} /></p>}

          <p className="mt-6 text-sm text-muted-foreground">
            <Link to="/plants" className="underline underline-offset-2">{t('garden.join.myGarden')}</Link>
          </p>
        </>
      )}
    </div>
  );
};

export default JoinGarden;
